'use client'

/**
 * Modal galerie souvenirs — style maison de luxe (Aman / Dior / Vogue).
 *
 * 2 vues dans une seule modal :
 *   • 'grid'     — galerie de miniatures organisée par section. Vue par
 *                  défaut au clic sur "Revivez l'événement". Tabs sticky
 *                  en haut pour smooth-scroll vers une section.
 *   • 'lightbox' — média plein écran avec navigation, télécharger, swipe.
 *                  Atteinte au clic sur une miniature. Bouton retour →
 *                  ramène à la grid au scroll position pré-clic.
 *
 * Design :
 *   - Fond noir profond #0a0a0a, pas de blur (UI lisible quelle que soit
 *     la lumière du média).
 *   - Or LDC #CBB68B pour accent (tabs actifs, séparateurs, controls).
 *   - Marges généreuses, jamais full-bleed agressif.
 *   - Custom video player en lightbox (pas de controls HTML5 natifs).
 *   - Animations : fade-in subtil, scale stagger sur les miniatures.
 *   - Lazy loading natif des miniatures (loading="lazy") → scale à 100+ photos.
 *
 * Trigger global : tout `<a href="#galerie">` ouvre la modal sans scroll
 * de la page (capture document.click).
 */

import { createPortal } from 'react-dom'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { EventGalleryShape, GalleryItem } from '@/lib/galleryShape'
import { HIGHLIGHTS_SECTION_ID, resolveSectionsForDisplay, type ResolvedSection } from '@/lib/galleryShape'
import type { SiteSliderImage } from '@/lib/siteSliderShape'
import BlurHashImg from './BlurHashImg'

const SWIPE_THRESHOLD_PX = 50

interface EventGalleryProps {
  gallery: EventGalleryShape
  /** Images du carrousel vedette. Si fourni, préfixe une section virtuelle
   *  "Highlights" dans le modal et active l'interception des clics sur
   *  les anchors #galerie-highlight-N (ouvre lightbox sur l'image N). */
  highlights?: SiteSliderImage[]
  /** Page galerie dédiée : ouvre la grille plein écran dès le montage, et
   *  « Fermer » revient à la page précédente (history.back) au lieu de
   *  masquer un fond vide. */
  autoOpen?: boolean
  /** Mode CONTRÔLÉ : quand `open` est fourni, la visibilité du modal suit ce
   *  prop et `onRequestClose` est appelé à la fermeture. Permet à un parent
   *  (ex. BandeauSouvenir) d'ouvrir la galerie de SA soirée en modal, sans
   *  passer par l'ancre globale #galerie. */
  open?: boolean
  onRequestClose?: () => void
}

type View = 'grid' | 'lightbox'

interface FlatItem {
  item: GalleryItem
  sectionIdx: number
  itemIdx: number
  section: ResolvedSection
}

export default function EventGallery({ gallery, highlights, autoOpen, open, onRequestClose }: EventGalleryProps) {
  const controlled = open !== undefined
  const sections = useMemo(() => resolveSectionsForDisplay(gallery, highlights), [gallery, highlights])

  /* Liste plate de tous les items avec leur position d'origine — sert à
     la lightbox pour naviguer cross-section sans recalculer. */
  const flat = useMemo<FlatItem[]>(() => {
    const arr: FlatItem[] = []
    sections.forEach((section, sectionIdx) => {
      section.items.forEach((item, itemIdx) => {
        arr.push({ item, sectionIdx, itemIdx, section })
      })
    })
    return arr
  }, [sections])

  const [mounted, setMounted] = useState(false)
  useEffect(() => { setMounted(true) }, [])

  const [internalOpen, setInternalOpen] = useState(false)
  /* En mode contrôlé, la visibilité suit le prop `open` ; sinon l'état interne
     piloté par l'ancre #galerie / autoOpen. */
  const isOpen = controlled ? !!open : internalOpen
  const [view, setView] = useState<View>('grid')
  const [flatIdx, setFlatIdx] = useState(0)

  /* Mémorise la scroll position de la grid au moment de l'ouverture
     d'une lightbox → on la restaure au retour. */
  const gridScrollPos = useRef(0)
  const gridScrollRef = useRef<HTMLDivElement | null>(null)

  /* ─── Trigger global a[href="#galerie"] ─── */
  const openGrid = useCallback(() => {
    if (sections.length === 0) return
    setView('grid')
    setInternalOpen(true)
  }, [sections.length])

  const close = useCallback(() => {
    if (controlled) {
      onRequestClose?.()
    } else if (autoOpen && typeof window !== 'undefined' && window.history.length > 1) {
      window.history.back()
    } else {
      setInternalOpen(false)
    }
  }, [controlled, onRequestClose, autoOpen])

  /* Page galerie dédiée : ouvre la grille plein écran au montage. En mode
     contrôlé, c'est le parent qui ouvre (on ignore autoOpen). */
  useEffect(() => {
    if (controlled) return
    if (autoOpen && sections.length > 0) {
      setView('grid')
      setInternalOpen(true)
    }
  }, [controlled, autoOpen, sections.length])

  /* Mode contrôlé : à chaque ouverture, on repart de la grille. */
  useEffect(() => {
    if (controlled && open) setView('grid')
  }, [controlled, open])

  const openLightboxAt = useCallback((idx: number) => {
    if (gridScrollRef.current) {
      gridScrollPos.current = gridScrollRef.current.scrollTop
    }
    setFlatIdx(idx)
    setView('lightbox')
  }, [])

  const backToGrid = useCallback(() => {
    setView('grid')
    /* Restore la scroll pos après le render de la grid. */
    requestAnimationFrame(() => {
      if (gridScrollRef.current) {
        gridScrollRef.current.scrollTop = gridScrollPos.current
      }
    })
  }, [])

  /* Ouvre directement la lightbox sur une image de la section Highlights.
     Utilisé par le clic sur une slide du carrousel vedette site (EventSiteSlider). */
  const openHighlightAt = useCallback((highlightIdx: number) => {
    const flatTargetIdx = flat.findIndex(
      f => f.section.id === HIGHLIGHTS_SECTION_ID && f.itemIdx === highlightIdx,
    )
    if (flatTargetIdx < 0) return
    /* Pas de scroll position grid à mémoriser : on ouvre depuis la page
       d'accueil, pas depuis la grid. Le "back to grid" depuis la lightbox
       affichera donc la grid scrollée en haut, ce qui est attendu. */
    gridScrollPos.current = 0
    setFlatIdx(flatTargetIdx)
    setView('lightbox')
    setInternalOpen(true)
  }, [flat])

  useEffect(() => {
    if (sections.length === 0) return
    /* Mode contrôlé : le parent ouvre lui-même le modal (son propre bouton),
       on n'intercepte donc pas les ancres #galerie de la page. */
    if (controlled) return
    const onDocClick = (e: MouseEvent) => {
      const el = e.target as HTMLElement | null
      if (!el) return

      /* Anchor "#galerie" → ouvre la grid (CTA "Revivez l'événement"). */
      if (el.closest('a[href="#galerie"]')) {
        e.preventDefault()
        openGrid()
        return
      }

      /* Anchor "#galerie-highlight-N" → ouvre directement la lightbox sur
         l'image N de la section Highlights (clic carrousel vedette). */
      const highlightAnchor = el.closest('a[href^="#galerie-highlight-"]') as HTMLAnchorElement | null
      if (highlightAnchor) {
        const m = (highlightAnchor.getAttribute('href') ?? '').match(/^#galerie-highlight-(\d+)$/)
        if (!m) return
        e.preventDefault()
        openHighlightAt(parseInt(m[1], 10))
      }
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [openGrid, openHighlightAt, sections.length, controlled])

  /* ─── Scroll lock + hide header (toujours, quelle que soit la vue) ─── */
  useEffect(() => {
    if (!isOpen) return
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    document.body.classList.add('demo-gallery-open')
    return () => {
      document.body.style.overflow = prevOverflow
      document.body.classList.remove('demo-gallery-open')
    }
  }, [isOpen])

  /* ─── Clavier ─── */
  useEffect(() => {
    if (!isOpen) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (view === 'lightbox') backToGrid()
        else close()
        return
      }
      if (view === 'lightbox') {
        if (e.key === 'ArrowLeft' && flatIdx > 0) setFlatIdx(flatIdx - 1)
        else if (e.key === 'ArrowRight' && flatIdx < flat.length - 1) setFlatIdx(flatIdx + 1)
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [isOpen, view, flatIdx, flat.length, backToGrid, close])

  if (!mounted || !isOpen) return null

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Galerie de l'événement"
      className="fixed inset-0 z-[9999] flex flex-col"
      style={{
        background: '#0a0a0a',
        animation: 'demo-fade-in 320ms cubic-bezier(0.16, 1, 0.3, 1)',
        fontFamily: 'var(--font-body, inherit)',
      }}
    >
      <style>{`
        @keyframes demo-fade-in { from { opacity: 0 } to { opacity: 1 } }
        @keyframes demo-grid-pop {
          from { opacity: 0; transform: translateY(8px) }
          to { opacity: 1; transform: translateY(0) }
        }
        @keyframes demo-media-in {
          from { opacity: 0; transform: scale(0.985) }
          to { opacity: 1; transform: scale(1) }
        }
        @keyframes demo-dropdown-in {
          from { opacity: 0; transform: translateY(-6px) }
          to { opacity: 1; transform: translateY(0) }
        }
        body.demo-gallery-open header { visibility: hidden !important; pointer-events: none !important; }
        body.demo-gallery-open [role="dialog"] header { visibility: visible !important; pointer-events: auto !important; }
        .demo-tab-underline {
          position: absolute; left: 0; right: 0; bottom: -6px;
          height: 1px; background: #CBB68B;
          transform: scaleX(0); transform-origin: center;
          transition: transform 320ms cubic-bezier(0.16, 1, 0.3, 1);
        }
        .demo-tab-active .demo-tab-underline { transform: scaleX(1); }
        .demo-tab:hover .demo-tab-underline { transform: scaleX(0.6); }
        .demo-thumb-img { transition: transform 480ms cubic-bezier(0.16, 1, 0.3, 1); }
        .demo-thumb:hover .demo-thumb-img { transform: scale(1.06); }
        .demo-thumb-overlay { opacity: 0; transition: opacity 240ms ease; }
        .demo-thumb:hover .demo-thumb-overlay { opacity: 1; }
        /* Masque la scrollbar verticale de la grille (et tabs horizontales) :
           le scroll reste fonctionnel, seul le track visuel est caché. */
        .demo-gallery-scroll { scrollbar-width: none; -ms-overflow-style: none; }
        .demo-gallery-scroll::-webkit-scrollbar { display: none; width: 0; height: 0; }
        /* Indique visuellement que la nav des sections scrolle horizontalement
           quand il y a plus de tabs que la largeur disponible (mobile). */
        .demo-tabs-fade {
          -webkit-mask-image: linear-gradient(to right, black 92%, transparent 100%);
          mask-image: linear-gradient(to right, black 92%, transparent 100%);
        }
      `}</style>

      {view === 'grid' ? (
        <GridView
          sections={sections}
          flat={flat}
          onPick={openLightboxAt}
          onClose={close}
          scrollRef={gridScrollRef}
        />
      ) : (
        <LightboxView
          flat={flat}
          index={flatIdx}
          onPrev={() => setFlatIdx(i => Math.max(0, i - 1))}
          onNext={() => setFlatIdx(i => Math.min(flat.length - 1, i + 1))}
          onBack={backToGrid}
          onClose={close}
        />
      )}
    </div>,
    document.body,
  )
}

/* ═════════════════════════════════════════════════════════════════════
   VIEW 1 — GRID (galerie de miniatures par section)
   ═════════════════════════════════════════════════════════════════════ */

interface GridViewProps {
  sections: ResolvedSection[]
  flat: FlatItem[]
  onPick: (flatIdx: number) => void
  onClose: () => void
  scrollRef: React.MutableRefObject<HTMLDivElement | null>
}

function GridView({ sections, flat, onPick, onClose, scrollRef }: GridViewProps) {
  const sectionRefs = useRef<Map<string, HTMLElement>>(new Map())

  const scrollToSection = useCallback((sectionId: string) => {
    const el = sectionRefs.current.get(sectionId)
    if (!el || !scrollRef.current) return
    /* Manual smooth scroll dans le container (pas window). */
    const container = scrollRef.current
    const top = el.offsetTop - container.offsetTop - 80 /* offset pour passer sous le sticky header */
    container.scrollTo({ top, behavior: 'smooth' })
  }, [scrollRef])

  /* Détecte la section active selon le scroll → met en valeur la tab. */
  const [activeSectionId, setActiveSectionId] = useState<string>(sections[0]?.id ?? '')

  /* Dropdown mobile (Dior-style) — remplace les tabs horizontaux sur petits
     écrans pour afficher toutes les sections accessibles sans scroll. */
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    if (!dropdownOpen) return
    const onDocClick = (e: MouseEvent) => {
      if (!dropdownRef.current?.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener('click', onDocClick)
    return () => document.removeEventListener('click', onDocClick)
  }, [dropdownOpen])
  useEffect(() => {
    const container = scrollRef.current
    if (!container) return
    const onScroll = () => {
      const top = container.scrollTop + 120
      let current = sections[0]?.id ?? ''
      for (const s of sections) {
        const el = sectionRefs.current.get(s.id)
        if (el && el.offsetTop - container.offsetTop <= top) {
          current = s.id
        }
      }
      setActiveSectionId(current)
    }
    container.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => container.removeEventListener('scroll', onScroll)
  }, [sections, scrollRef])

  /* Index plat par (sectionIdx, itemIdx) — pour passer le bon flatIdx à onPick. */
  const flatIndexFor = useCallback((sectionIdx: number, itemIdx: number): number => {
    return flat.findIndex(f => f.sectionIdx === sectionIdx && f.itemIdx === itemIdx)
  }, [flat])

  const total = flat.length

  return (
    <>
      {/* ═══════════════ STICKY HEADER ═══════════════ */}
      <header
        className="relative flex-shrink-0 px-6 md:px-12 pt-7 md:pt-9 pb-4"
        style={{
          background: 'linear-gradient(to bottom, #0a0a0a 0%, #0a0a0a 80%, rgba(10,10,10,0) 100%)',
        }}
      >
        <div className="flex items-start justify-between gap-6">
          {/* Titre */}
          <div>
            <p
              className="uppercase font-medium"
              style={{
                color: '#CBB68B',
                fontSize: 10,
                letterSpacing: '0.32em',
                marginBottom: 6,
              }}
            >
              Revivez l&apos;événement
            </p>
            <p
              className="italic"
              style={{
                fontFamily: 'var(--font-heading, var(--font-body, serif))',
                fontWeight: 400,
                fontSize: 'clamp(14px, 0.85rem + 0.2vw, 18px)',
                letterSpacing: '0.01em',
                color: 'rgba(245,240,232,0.7)',
              }}
            >
              {total} souvenir{total > 1 ? 's' : ''}
            </p>
          </div>

          {/* Bouton fermer */}
          <button
            type="button"
            onClick={onClose}
            aria-label="Fermer"
            style={iconBtnStyle()}
            className="shrink-0"
          >
            <span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>×</span>
          </button>
        </div>

        {/* Mobile : dropdown sélecteur (Dior-style) — section active visible
            comme un selector cliquable, dropdown au tap pour voir toutes les
            sections. Plus de scroll horizontal, plus d'ambiguïté. */}
        <div className="md:hidden mt-6 relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setDropdownOpen(o => !o)}
            aria-haspopup="listbox"
            aria-expanded={dropdownOpen}
            style={{
              width: '100%',
              padding: '14px 18px',
              border: '1px solid rgba(201,168,106,0.28)',
              background: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              cursor: 'pointer',
              fontFamily: 'inherit',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
              <span style={{
                color: '#CBB68B',
                fontSize: 11,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                fontWeight: 500,
              }}>
                {sections.find(s => s.id === activeSectionId)?.title ?? sections[0]?.title ?? ''}
              </span>
              <span style={{
                color: 'rgba(245,240,232,0.55)',
                fontStyle: 'italic',
                fontFamily: 'var(--font-heading, var(--font-body, serif))',
                fontSize: 13,
              }}>
                {(() => {
                  const n = sections.find(s => s.id === activeSectionId)?.items.length ?? sections[0]?.items.length ?? 0
                  return `${n} souvenir${n > 1 ? 's' : ''}`
                })()}
              </span>
            </div>
            <span aria-hidden style={{
              color: '#CBB68B',
              fontSize: 14,
              transition: 'transform 240ms cubic-bezier(0.16, 1, 0.3, 1)',
              transform: dropdownOpen ? 'rotate(180deg)' : 'rotate(0)',
              display: 'inline-block',
              lineHeight: 1,
            }}>▾</span>
          </button>

          {dropdownOpen && (
            <ul
              role="listbox"
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: '#141414',
                border: '1px solid rgba(201,168,106,0.28)',
                borderTop: 'none',
                zIndex: 10,
                maxHeight: '60vh',
                overflowY: 'auto',
                listStyle: 'none',
                margin: 0,
                padding: 0,
                animation: 'demo-dropdown-in 200ms cubic-bezier(0.16, 1, 0.3, 1)',
              }}
            >
              {sections.filter(s => s.id !== activeSectionId).map((s) => (
                <li key={s.id} role="option" aria-selected="false">
                  <button
                    type="button"
                    onClick={() => {
                      setDropdownOpen(false)
                      scrollToSection(s.id)
                    }}
                    style={{
                      width: '100%',
                      padding: '14px 18px',
                      background: 'transparent',
                      border: 'none',
                      borderBottom: '1px solid rgba(201,168,106,0.06)',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      fontFamily: 'inherit',
                      textAlign: 'left',
                    }}
                  >
                    <span style={{
                      color: 'rgba(245,240,232,0.85)',
                      fontSize: 11,
                      letterSpacing: '0.28em',
                      textTransform: 'uppercase',
                      fontWeight: 500,
                    }}>
                      {s.title}
                    </span>
                    <span style={{
                      color: 'rgba(201,168,106,0.55)',
                      fontStyle: 'italic',
                      fontFamily: 'var(--font-heading, var(--font-body, serif))',
                      fontSize: 12,
                    }}>
                      {String(s.items.length).padStart(2, '0')}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Desktop : tabs sections (caché sur mobile, remplacé par le dropdown
            ci-dessus). `flex-wrap` : avec beaucoup d'éditions aux titres longs,
            les onglets passent à la ligne au lieu de déborder hors écran —
            toutes les sections restent visibles et cliquables. */}
        <nav
          className="hidden md:flex md:flex-wrap items-center gap-x-8 gap-y-3 mt-6"
        >
          {sections.map((s) => (
            <button
              key={s.id}
              type="button"
              onClick={() => scrollToSection(s.id)}
              className={`demo-tab relative shrink-0 ${s.id === activeSectionId ? 'demo-tab-active' : ''}`}
              style={{
                padding: '6px 0',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                color: s.id === activeSectionId ? '#CBB68B' : 'rgba(245,240,232,0.5)',
                fontSize: 11,
                fontWeight: 500,
                letterSpacing: '0.32em',
                textTransform: 'uppercase',
                transition: 'color 240ms ease',
                whiteSpace: 'nowrap',
              }}
              aria-current={s.id === activeSectionId ? 'true' : 'false'}
            >
              {s.title} <span style={{ opacity: 0.55, marginLeft: 6, fontSize: 9 }}>{s.items.length}</span>
              <span className="demo-tab-underline" />
            </button>
          ))}
        </nav>
      </header>

      {/* ═══════════════ GRID SCROLLABLE ═══════════════
          data-lenis-prevent : indispensable — sinon Lenis (monté global
          dans layout.tsx) intercepte les events wheel/touch et le scroll
          de ce container ne fonctionne pas du tout. */}
      <div
        ref={scrollRef}
        data-lenis-prevent="true"
        className="demo-gallery-scroll flex-1 overflow-y-auto"
        style={{ paddingBottom: 60, minHeight: 0 }}
      >
        <div className="px-6 md:px-12 pt-2">
          {sections.map((section, sectionIdx) => (
            <section
              key={section.id}
              ref={(el) => {
                if (el) sectionRefs.current.set(section.id, el)
                else sectionRefs.current.delete(section.id)
              }}
              className="mb-12 md:mb-16"
            >
              {/* Header de section — trait or + titre + compteur */}
              <div className="flex items-baseline gap-4 mb-5 md:mb-7">
                <h2
                  className="uppercase font-medium"
                  style={{
                    color: 'rgba(245,240,232,0.95)',
                    fontSize: 12,
                    letterSpacing: '0.32em',
                  }}
                >
                  {section.title}
                </h2>
                <div className="flex-1" style={{ height: 1, background: 'rgba(201,168,106,0.28)' }} />
                <span
                  className="tabular-nums italic"
                  style={{
                    fontFamily: 'var(--font-heading, var(--font-body, serif))',
                    fontWeight: 400,
                    fontSize: 13,
                    color: 'rgba(245,240,232,0.55)',
                  }}
                >
                  {section.items.length} souvenir{section.items.length > 1 ? 's' : ''}
                </span>
              </div>

              {/* Masonry de miniatures : chaque vignette prend la forme réelle de
                  son image (portrait/paysage) — plus aucun rognage. */}
              <div className="columns-2 sm:columns-3 md:columns-4 lg:columns-5 xl:columns-6" style={{ columnGap: '0.6rem' }}>
                {section.items.map((item, itemIdx) => (
                  <ThumbnailButton
                    key={item.id}
                    item={item}
                    onClick={() => onPick(flatIndexFor(sectionIdx, itemIdx))}
                    delayMs={Math.min(itemIdx * 28, 240)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </>
  )
}

interface ThumbnailButtonProps {
  item: GalleryItem
  onClick: () => void
  delayMs: number
}

function ThumbnailButton({ item, onClick, delayMs }: ThumbnailButtonProps) {
  const isVideo = item.type === 'video'
  /* Pour la grille on utilise prioritairement la variante thumbnail WebP 600px
     (générée par le pipeline Sharp à l'upload) si disponible — sinon fallback
     sur l'original. Items uploadés avant Phase 1 n'ont pas de thumbnail. */
  const imgSrc = item.thumbnail ?? item.url
  const videoPosterSrc = item.poster ?? `${item.url}#t=0.5`

  /* Photo « resserrée » (item.pos défini) → case carrée cadrée sur le point focal.
     Sinon → forme réelle : ratio fourni, sinon la vraie forme mesurée au
     chargement (évite le rognage quand aucune forme n'est connue). */
  const ratioFourni = item.ratio ?? (item.width && item.height ? item.width / item.height : undefined)
  const [ratioMesure, setRatioMesure] = useState<number | null>(null)
  const aspectRatio = item.pos ? 1 : (ratioFourni ?? ratioMesure ?? 1)

  return (
    <button
      type="button"
      onClick={onClick}
      className="demo-thumb relative overflow-hidden w-full break-inside-avoid mb-2 md:mb-3"
      style={{
        aspectRatio: String(aspectRatio),
        background: '#111',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        animation: `demo-grid-pop 360ms cubic-bezier(0.16, 1, 0.3, 1) ${delayMs}ms backwards`,
      }}
      aria-label={item.alt ?? (isVideo ? 'Voir la vidéo' : 'Voir la photo')}
    >
      {isVideo && item.poster ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={videoPosterSrc}
          alt=""
          loading="lazy"
          className="demo-thumb-img absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: item.pos ?? '50% 50%' }}
        />
      ) : isVideo ? (
        /* Pas de poster custom → on utilise <video preload=metadata> + #t=0.5
           pour afficher une frame statique sans charger toute la vidéo. */
        <video
          src={videoPosterSrc}
          muted
          playsInline
          preload="metadata"
          className="demo-thumb-img absolute inset-0 w-full h-full object-cover"
          style={{ objectPosition: item.pos ?? '50% 50%' }}
        />
      ) : (
        /* Image standard : BlurHashImg = placeholder canvas décodé + fade-in
           de l'image quand elle finit de charger. ~10x perçu plus rapide. */
        <BlurHashImg
          src={imgSrc}
          alt={item.alt ?? ''}
          blurhash={item.blurhash}
          loading="lazy"
          className="demo-thumb-img absolute inset-0 w-full h-full"
          objectPosition={item.pos ?? '50% 50%'}
          onNaturalSize={!item.pos && ratioFourni == null
            ? (w, h) => setRatioMesure(w / h)
            : undefined}
        />
      )}

      {/* Voile au hover */}
      <div
        className="demo-thumb-overlay absolute inset-0"
        style={{
          background: 'linear-gradient(to bottom, rgba(0,0,0,0) 50%, rgba(0,0,0,0.4) 100%)',
        }}
        aria-hidden
      />

      {/* Badge vidéo */}
      {isVideo && (
        <span
          className="absolute"
          style={{
            bottom: 8,
            right: 8,
            width: 24,
            height: 24,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.6)',
            borderRadius: 999,
            border: '1px solid rgba(201,168,106,0.6)',
            color: '#CBB68B',
            fontSize: 10,
            paddingLeft: 2,
          }}
          aria-hidden
        >
          ▶
        </span>
      )}
    </button>
  )
}

/* ═════════════════════════════════════════════════════════════════════
   VIEW 2 — LIGHTBOX (média plein écran)
   ═════════════════════════════════════════════════════════════════════ */

interface LightboxViewProps {
  flat: FlatItem[]
  index: number
  onPrev: () => void
  onNext: () => void
  onBack: () => void
  onClose: () => void
}

function LightboxView({ flat, index, onPrev, onNext, onBack, onClose }: LightboxViewProps) {
  const current = flat[index]
  if (!current) return null
  const isVideo = current.item.type === 'video'

  /* ── Swipe horizontal sur le viewport image ── */
  const touchStart = useRef<{ x: number; y: number } | null>(null)
  const onTouchStart = (e: React.TouchEvent) => {
    const t = e.touches[0]
    touchStart.current = { x: t.clientX, y: t.clientY }
  }
  const onTouchEnd = (e: React.TouchEvent) => {
    const start = touchStart.current
    if (!start) return
    const t = e.changedTouches[0]
    const dx = t.clientX - start.x
    const dy = t.clientY - start.y
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > SWIPE_THRESHOLD_PX) {
      if (dx > 0) onPrev(); else onNext()
    }
    touchStart.current = null
  }

  return (
    <>
      {/* ═══════════════ TOP BAR ═══════════════ */}
      <header
        className="relative flex items-center justify-between px-6 md:px-12 pt-7 md:pt-9 flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Back to grid */}
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-3"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'rgba(245,240,232,0.7)',
            fontSize: 10,
            letterSpacing: '0.32em',
            textTransform: 'uppercase',
            transition: 'color 240ms ease',
            padding: 0,
          }}
          aria-label="Retour à la galerie"
        >
          <span aria-hidden style={{ fontSize: 14 }}>‹</span>
          Galerie
        </button>

        {/* Section + index */}
        <span
          className="italic hidden md:inline"
          style={{
            fontFamily: 'var(--font-heading, var(--font-body, serif))',
            fontWeight: 400,
            fontSize: 'clamp(13px, 0.8rem + 0.15vw, 16px)',
            color: 'rgba(245,240,232,0.55)',
          }}
        >
          {current.section.title.toLowerCase()}
          <span style={{ color: '#CBB68B', margin: '0 12px' }}>·</span>
          <span className="tabular-nums">
            {String(current.itemIdx + 1).padStart(2, '0')}{' '}
            <span style={{ color: '#CBB68B' }}>—</span>{' '}
            {String(current.section.items.length).padStart(2, '0')}
          </span>
        </span>

        {/* Actions */}
        <div className="flex items-center gap-3 shrink-0">
          <a
            href={current.item.url}
            download
            onClick={(e) => e.stopPropagation()}
            aria-label="Télécharger"
            style={iconBtnStyle()}
          >
            <span aria-hidden style={{ fontSize: 14 }}>↓</span>
          </a>
          <button
            type="button"
            onClick={onBack}
            aria-label="Retour à la galerie"
            style={iconBtnStyle()}
          >
            <span aria-hidden style={{ fontSize: 16, lineHeight: 1 }}>×</span>
          </button>
        </div>
      </header>

      {/* ═══════════════ VIEWPORT ═══════════════ */}
      <main
        className="relative flex-1 flex items-center justify-center px-6 md:px-20"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        style={{ minHeight: 0 }}
      >
        {/* Flèche prev */}
        {index > 0 && (
          <button
            type="button"
            onClick={onPrev}
            className="hidden md:flex absolute left-4 lg:left-10 top-1/2 -translate-y-1/2 z-20"
            style={navArrowStyle()}
            aria-label="Précédent"
          >
            ‹
          </button>
        )}

        <div
          key={`${index}-${current.item.id}`}
          className={`relative flex items-center justify-center ${
            isVideo
              ? 'max-w-[95vw] md:max-w-[min(64vw,720px)]'
              : 'max-w-[96vw] md:max-w-[min(88vw,1400px)]'
          }`}
          style={{
            /* Anciennement height: 82vh/80vh — sur mobile cette hauteur fixe
               débordait sur le header (back/close cut). On laisse flexbox
               sizer : le parent <main> est flex-1 donc il occupe l'espace
               entre header (top) et footer (bottom mobile). 100% fill le
               flex sans déborder. Sur desktop, idem (pas de footer mobile
               donc la zone main est plus haute). L'image/vidéo a déjà
               objectFit:contain + maxWidth/maxHeight 100% donc elle scale
               pour rester dans le container. */
            width: '100%',
            height: '100%',
            animation: 'demo-media-in 360ms cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          {isVideo ? (
            <CustomVideoPlayer
              key={current.item.url}
              src={current.item.url}
              poster={current.item.poster}
            />
          ) : (
            /* Lightbox : preview WebP 2048px en priorité (généré par Sharp),
               fallback sur l'original si pas de variante. Le download link
               (header) pointe toujours sur l'original HD. */
            // eslint-disable-next-line @next/next/no-img-element
            <img
              src={current.item.preview ?? current.item.url}
              alt={current.item.alt ?? ''}
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '100%',
                width: 'auto',
                height: 'auto',
                objectFit: 'contain',
              }}
            />
          )}
        </div>

        {/* Flèche next */}
        {index < flat.length - 1 && (
          <button
            type="button"
            onClick={onNext}
            className="hidden md:flex absolute right-4 lg:right-10 top-1/2 -translate-y-1/2 z-20"
            style={navArrowStyle()}
            aria-label="Suivant"
          >
            ›
          </button>
        )}
      </main>

      {/* ═══════════════ FOOTER mobile ═══════════════ */}
      <footer
        className="md:hidden relative flex items-center justify-center px-6 pb-8 pt-4 select-none flex-shrink-0"
        onClick={(e) => e.stopPropagation()}
      >
        <span
          className="italic"
          style={{
            fontFamily: 'var(--font-heading, var(--font-body, serif))',
            fontWeight: 400,
            fontSize: 13,
            color: 'rgba(245,240,232,0.7)',
          }}
        >
          {current.section.title.toLowerCase()}
          <span style={{ color: '#CBB68B', margin: '0 10px' }}>·</span>
          <span className="tabular-nums">
            {String(current.itemIdx + 1).padStart(2, '0')} <span style={{ color: '#CBB68B' }}>—</span>{' '}
            {String(current.section.items.length).padStart(2, '0')}
          </span>
        </span>
      </footer>
    </>
  )
}

/* ═════════════════════════════════════════════════════════════════════
   Custom video player — minimaliste, style maison
   ═════════════════════════════════════════════════════════════════════ */

interface CustomVideoPlayerProps {
  src: string
  poster?: string
}

function CustomVideoPlayer({ src, poster }: CustomVideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [playing, setPlaying] = useState(false)
  const [muted, setMuted] = useState(true)
  const [volume, setVolume] = useState(1)
  const [progress, setProgress] = useState(0)
  const [showOverlay, setShowOverlay] = useState(true)
  const hideTimer = useRef<number | null>(null)

  const togglePlay = () => {
    const v = videoRef.current
    if (!v) return
    if (v.paused) v.play().catch(() => {})
    else v.pause()
  }

  const toggleMute = () => {
    const v = videoRef.current
    if (!v) return
    v.muted = !v.muted
    setMuted(v.muted)
    if (!v.muted && v.volume === 0) {
      v.volume = 0.5
      setVolume(0.5)
    }
  }

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const v = videoRef.current
    if (!v) return
    const newVol = parseFloat(e.target.value)
    v.volume = newVol
    setVolume(newVol)
    if (newVol > 0 && v.muted) {
      v.muted = false
      setMuted(false)
    }
    if (newVol === 0 && !v.muted) {
      v.muted = true
      setMuted(true)
    }
  }

  const seek = (e: React.MouseEvent<HTMLDivElement>) => {
    const v = videoRef.current
    if (!v || !v.duration) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = (e.clientX - rect.left) / rect.width
    v.currentTime = Math.max(0, Math.min(v.duration, ratio * v.duration))
  }

  const showOverlayTemporary = () => {
    setShowOverlay(true)
    if (hideTimer.current) window.clearTimeout(hideTimer.current)
    hideTimer.current = window.setTimeout(() => setShowOverlay(false), 3500)
  }

  useEffect(() => {
    return () => {
      if (hideTimer.current) window.clearTimeout(hideTimer.current)
    }
  }, [])

  return (
    <div
      className="relative w-full h-full"
      onMouseMove={showOverlayTemporary}
      onTouchStart={showOverlayTemporary}
    >
      <style>{`
        input[type=range].demo-vol {
          -webkit-appearance: none;
          appearance: none;
          background: transparent;
          height: 14px;
          margin: 0; padding: 0;
          cursor: pointer;
          width: 100%;
        }
        input[type=range].demo-vol:focus { outline: none; }
        input[type=range].demo-vol::-webkit-slider-runnable-track {
          height: 1px;
          background: linear-gradient(to right,
            #CBB68B 0%, #CBB68B var(--demo-vol-pct, 100%),
            rgba(245,240,232,0.28) var(--demo-vol-pct, 100%), rgba(245,240,232,0.28) 100%);
          border-radius: 999px;
        }
        input[type=range].demo-vol::-webkit-slider-thumb {
          -webkit-appearance: none;
          appearance: none;
          width: 8px; height: 8px;
          border-radius: 999px;
          background: #CBB68B;
          margin-top: -3.5px;
          box-shadow: 0 0 6px rgba(201,168,106,0.5);
          border: none;
        }
        input[type=range].demo-vol::-moz-range-track {
          height: 1px;
          background: rgba(245,240,232,0.28);
          border-radius: 999px;
        }
        input[type=range].demo-vol::-moz-range-progress {
          height: 1px;
          background: #CBB68B;
          border-radius: 999px;
        }
        input[type=range].demo-vol::-moz-range-thumb {
          width: 8px; height: 8px;
          border-radius: 999px;
          background: #CBB68B;
          border: none;
          box-shadow: 0 0 6px rgba(201,168,106,0.5);
        }
      `}</style>

      <video
        ref={videoRef}
        src={src}
        poster={poster}
        autoPlay
        muted
        playsInline
        preload="auto"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onTimeUpdate={(e) => {
          const v = e.currentTarget
          if (v.duration && isFinite(v.duration)) {
            setProgress(v.currentTime / v.duration)
          }
        }}
        onClick={togglePlay}
        className="w-full h-full object-contain"
        style={{ background: '#000', cursor: 'pointer' }}
      />

      {(!playing || showOverlay) && (
        <button
          type="button"
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center"
          style={{ background: 'transparent', border: 'none' }}
          aria-label={playing ? 'Pause' : 'Lecture'}
        >
          <span
            className="flex items-center justify-center"
            style={{
              width: 64, height: 64,
              borderRadius: 999,
              background: 'rgba(0,0,0,0.45)',
              backdropFilter: 'blur(6px)',
              WebkitBackdropFilter: 'blur(6px)',
              border: '1px solid rgba(201,168,106,0.6)',
              color: '#CBB68B',
              fontSize: 22,
              opacity: playing ? 0.85 : 1,
            }}
          >
            {playing ? '❚❚' : '▶'}
          </span>
        </button>
      )}

      <div
        className="absolute left-0 right-0 bottom-0 pointer-events-none"
        style={{
          height: 80,
          background: 'linear-gradient(to top, rgba(0,0,0,0.55) 0%, rgba(0,0,0,0.25) 50%, rgba(0,0,0,0) 100%)',
        }}
      />

      <div
        className="absolute left-0 right-0 bottom-0 flex items-center gap-4 px-5 pb-4"
        style={{ pointerEvents: 'none' }}
      >
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); toggleMute() }}
          aria-label={muted ? 'Activer le son' : 'Couper le son'}
          style={{
            pointerEvents: 'auto',
            width: 24, height: 24,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            background: 'transparent',
            border: 'none',
            color: '#CBB68B',
            padding: 0,
            cursor: 'pointer',
            opacity: 0.95,
            flexShrink: 0,
          }}
        >
          <VolumeIcon muted={muted} volume={volume} />
        </button>

        <div
          className="hidden md:flex items-center"
          style={{ pointerEvents: 'auto', width: 88, flexShrink: 0 }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={muted ? 0 : volume}
            onChange={handleVolumeChange}
            aria-label="Volume"
            className="demo-vol"
            style={{
              ['--demo-vol-pct' as string]: `${(muted ? 0 : volume) * 100}%`,
            }}
          />
        </div>

        <div
          onClick={(e) => { e.stopPropagation(); seek(e) }}
          className="flex-1"
          style={{
            pointerEvents: 'auto',
            height: 14,
            display: 'flex', alignItems: 'center',
            cursor: 'pointer',
            minWidth: 40,
          }}
        >
          <div style={{
            width: '100%',
            height: 1,
            background: 'rgba(245,240,232,0.28)',
            borderRadius: 999,
            overflow: 'hidden',
          }}>
            <div style={{
              width: `${progress * 100}%`,
              height: '100%',
              background: '#CBB68B',
              transition: 'width 120ms linear',
            }} />
          </div>
        </div>
      </div>
    </div>
  )
}

function VolumeIcon({ muted, volume }: { muted: boolean; volume: number }) {
  const effectiveVolume = muted ? 0 : volume
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <path d="M3 9.5v5h3.5L11 18V6L6.5 9.5H3Z" />
      {effectiveVolume === 0 ? (
        <>
          <line x1="15" y1="9" x2="20" y2="14" />
          <line x1="20" y1="9" x2="15" y2="14" />
        </>
      ) : (
        <>
          <path d="M14.5 9.5c1 .9 1.6 2 1.6 3s-.6 2.1-1.6 3" />
          {effectiveVolume > 0.5 && (
            <path d="M17 7c1.8 1.4 3 3.1 3 5s-1.2 3.6-3 5" />
          )}
        </>
      )}
    </svg>
  )
}

function iconBtnStyle(): React.CSSProperties {
  return {
    width: 34, height: 34,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    background: 'transparent',
    border: '1px solid rgba(201,168,106,0.55)',
    borderRadius: 999,
    color: '#CBB68B',
    cursor: 'pointer',
    transition: 'background 240ms ease, color 240ms ease, border-color 240ms ease',
  }
}

function navArrowStyle(): React.CSSProperties {
  return {
    width: 44, height: 44,
    background: 'transparent',
    border: '1px solid rgba(201,168,106,0.45)',
    borderRadius: 999,
    color: '#CBB68B',
    fontSize: 20, lineHeight: 1,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer',
    transition: 'border-color 240ms ease, color 240ms ease',
  }
}
